import {
  AdjustmentMode,
  BoundOperator,
  findRangeRowIssues,
  getPercentageBasis,
  PercentageBasis,
  ProductPriceBasisType,
  QualityFormulaType,
  RangeRowIssueType,
  type SpecType,
  TierMode,
} from '@local/lib';

import type { ProductPriceBasisState, RangeFormulaRow, SpecState, ThresholdFormulaRow } from './qualityFormulaTypes';

import { AMOUNT_CONFIG_BY_FORMULA_TYPE, isMultiRowFormulaType, SPEC_TYPES } from './qualityFormulaMetadata';

/**
 * Compact, business-readable summary of one Range row's interval, e.g. "3 < Actual ≤ 6" —
 * reflects each bound's own operator (real legacy formulas mix inclusive/exclusive per row).
 */
export const getRangeRowExpressionLabel = (row: RangeFormulaRow): string => {
  const lowerSymbol = row.lowerBoundOperator === BoundOperator.Inclusive ? '≤' : '<';
  if (row.upperBound === null) {
    const lowerCompSymbol = row.lowerBoundOperator === BoundOperator.Inclusive ? '≥' : '>';
    return `Actual ${lowerCompSymbol} ${row.lowerBound}`;
  }
  const upperSymbol = row.upperBoundOperator === BoundOperator.Inclusive ? '≤' : '<';
  return `${row.lowerBound} ${lowerSymbol} Actual ${upperSymbol} ${row.upperBound}`;
};

export interface RangeGapInfo {
  aboveLastRange: null | string;
  belowFirstRange: null | string;
}

/**
 * Surfaces the two spots a Range configuration can leave unconfigured — below the first row's
 * floor and above the last row's ceiling (when it isn't left unlimited) — purely informational,
 * not a validation error, since an intentional "no adjustment applies" zone is valid business data.
 */
export const getRangeGapInfo = (rows: RangeFormulaRow[]): RangeGapInfo => {
  const first = rows[0];
  const last = rows[rows.length - 1];
  return {
    aboveLastRange: last.upperBound === null ? null : `Actual > ${last.upperBound} has no configured adjustment`,
    belowFirstRange: `Actual ≤ ${first.lowerBound} has no configured adjustment`,
  };
};

export interface RangeBoundaryIssue {
  boundaryValue: string;
  message: string;
  rowIndex: number;
  type: 'gap' | 'overlap';
}

/**
 * Builds this pair's display message once `findRangeRowIssues` (the shared classifier also used
 * by the persisted/calculate-endpoint schemas) has already decided what relation applies — kept
 * here, not in the shared lib, since wording is a UI presentation concern. `unsorted` surfaces as
 * `overlap` (same red, blocking treatment): submitting rows out of order is the same ambiguity a
 * true overlap is, just not yet expressed as one.
 */
const describeRowPairIssue = (
  upperRow: RangeFormulaRow,
  lowerRow: RangeFormulaRow,
  rowIndex: number,
  type: RangeRowIssueType,
): RangeBoundaryIssue => {
  if (type === RangeRowIssueType.Unsorted) {
    return {
      boundaryValue: `${lowerRow.lowerBound}`,
      message: `Range ${rowIndex + 2} starts before Range ${rowIndex + 1} — reorder them or adjust their bounds.`,
      rowIndex: rowIndex + 1,
      type: 'overlap',
    };
  }

  const atSharedBoundary = upperRow.upperBound === lowerRow.lowerBound;
  if (type === RangeRowIssueType.Overlap) {
    return {
      boundaryValue: atSharedBoundary ? `${upperRow.upperBound}` : `${lowerRow.lowerBound}–${upperRow.upperBound}`,
      message: atSharedBoundary
        ? `Actual = ${upperRow.upperBound} matches both Range ${rowIndex + 1} and Range ${rowIndex + 2} — make one side exclusive.`
        : `Range ${rowIndex + 1} and Range ${rowIndex + 2} overlap between ${lowerRow.lowerBound} and ${upperRow.upperBound} — adjust one of their bounds.`,
      rowIndex: rowIndex + 1,
      type: 'overlap',
    };
  }
  return {
    boundaryValue: atSharedBoundary ? `${upperRow.upperBound}` : `${upperRow.upperBound}–${lowerRow.lowerBound}`,
    message: atSharedBoundary
      ? `Actual = ${upperRow.upperBound} has no configured adjustment — falls between Range ${rowIndex + 1} and Range ${rowIndex + 2}.`
      : `Actual values between ${upperRow.upperBound} and ${lowerRow.lowerBound} have no configured adjustment.`,
    rowIndex: rowIndex + 1,
    type: 'gap',
  };
};

// A new row's lower bound only *defaults* to the previous row's upper bound (see handleAddRow) —
// after that, both are freely editable, so adjacent rows can end up with an actual interval gap,
// overlap, or reversed order, not just a mismatched shared point — see `findRangeRowIssues` (the
// same classifier the persisted/calculate-endpoint schemas enforce) for what each relation means.
export const getRangeBoundaryIssues = (rows: RangeFormulaRow[]): RangeBoundaryIssue[] =>
  findRangeRowIssues(rows).map(({ rowIndex, type }) =>
    describeRowPairIssue(rows[rowIndex], rows[rowIndex + 1], rowIndex, type),
  );

export interface FormulaRowValidation {
  amount?: string;
  perIncrement?: string;
  upperBound?: string;
}

/**
 * Amount-only validation, shared by Threshold and Range rows alike (both carry the same rate
 * fields) — catches a zero-value rate (or, for a scaled row, a zero-value increment divisor)
 * before running a preview, rather than only finding out from a confusing API result. A flat
 * row ignores perIncrement entirely, so it's never flagged there.
 */
export const validateRowAmount = (row: ThresholdFormulaRow, formulaType: QualityFormulaType): FormulaRowValidation => {
  const errors: FormulaRowValidation = {};

  if (formulaType === QualityFormulaType.PerUnit && row.qualityPricePerUnit === 0) {
    errors.amount = 'Enter a non-zero rate';
  }
  if (formulaType === QualityFormulaType.Percentage && row.percentFactor === 0) {
    errors.amount = 'Enter a non-zero factor';
  }
  if (!row.isFlatAdjustment && row.perIncrement === 0) {
    errors.perIncrement = 'Enter a non-zero increment';
  }

  return errors;
};

/**
 * Range-only validation — adds the one bounds check that still applies once every field is
 * guaranteed a real number (an upper bound at or below the lower bound) on top of the shared
 * amount check above; Threshold rows have no bounds to validate. A `null` upperBound (no upper
 * limit) is always valid — there's no bound to check.
 */
export const validateRangeRow = (row: RangeFormulaRow, formulaType: QualityFormulaType): FormulaRowValidation => {
  const errors: FormulaRowValidation = { ...validateRowAmount(row, formulaType) };

  if (row.upperBound !== null && row.upperBound <= row.lowerBound) {
    errors.upperBound = 'Must be greater than the lower bound';
  }

  return errors;
};

/**
 * Mirrors the one contractedSpec business rule the shared Zod schema enforces beyond "is a
 * number" (CalculateAdjustmentsRequestSchema's superRefine, in @local/lib's QualityDto.ts): a
 * Percentage spec whose basis is relative to its own contracted value (BTU/HGI/KCAL) divides by
 * it, so zero isn't just a wrong guess, it's undefined. Reuses the same specType categorization
 * (getPercentageBasis) the schema's own specType set was built from, rather than re-deriving
 * which specs count as "relative" independently.
 */
export const validateContractedSpec = (
  contractedSpec: number,
  specType: SpecType,
  formulaType: QualityFormulaType,
): string | undefined => {
  const isRelativePercentage =
    formulaType === QualityFormulaType.Percentage &&
    getPercentageBasis(specType) === PercentageBasis.RelativeToContracted;
  if (isRelativePercentage && contractedSpec === 0) return 'Must not be zero for this spec';
  return undefined;
};

/**
 * Product Price Basis only has further fields to validate for two of its three options — Price
 * Components needs at least one selected component (otherwise there is nothing to scale against),
 * and Deemed Price needs a positive override value. Contract Price has no further fields.
 */
export const validateProductPriceBasis = (priceBasis: ProductPriceBasisState): string | undefined => {
  if (priceBasis.type === ProductPriceBasisType.PriceComponents && priceBasis.selectedComponentKeys.length === 0) {
    return 'Select at least one price component';
  }
  if (priceBasis.type === ProductPriceBasisType.DeemedPrice && priceBasis.deemedPrice <= 0) {
    return 'Enter a positive deemed price';
  }
  return undefined;
};

export interface MissingFieldEntry {
  fieldLabel: string;
  reason: string;
  specLabel: string;
  specType: SpecType;
}

/**
 * Runs the business-rule validation used for the red-bordered inputs (validateRowAmount /
 * validateRangeRow) against one spec's current state, regardless of its `enabled` flag — the one
 * place this logic lives, shared by `validateEnabledSpecStates` (below, across every enabled spec)
 * and anywhere that only needs a single spec's own status (e.g. the focused editor's "Configured"
 * badge, the navigator's per-row status). Only Percentage/PerUnit specs have anything to check
 * here — every other field on every formula type is a plain, always-populated `number` by the time
 * it reaches this state (see qualityFormulaDefaults/specFormSlice's input-layer gating), and the
 * single-row formula types' own business rules (e.g. adjusted-sales-price can't be exactly 100)
 * live solely in the shared Zod schema — see calculateQualityAdjustments's pre-flight parse and
 * describeCalculateAdjustmentsValidationError.
 */
export const getSpecValidationIssues = (specType: SpecType, state: SpecState): MissingFieldEntry[] => {
  const missingFields: MissingFieldEntry[] = [];
  if (!isMultiRowFormulaType(state.formulaType)) return missingFields;

  const spec = SPEC_TYPES.find(candidate => candidate.key === specType);
  const specLabel = spec?.label ?? specType;
  const { formulaType } = state;
  const amountConfig = AMOUNT_CONFIG_BY_FORMULA_TYPE[formulaType];
  const isThreshold = state.tierMode === TierMode.Threshold;

  const contractedSpecError = validateContractedSpec(state.contractedSpec, specType, formulaType);
  if (contractedSpecError) {
    missingFields.push({ fieldLabel: 'Contracted Value', reason: contractedSpecError, specLabel, specType });
  }

  if (formulaType === QualityFormulaType.Percentage) {
    const priceBasisError = validateProductPriceBasis(state.priceBasis);
    if (priceBasisError) {
      missingFields.push({ fieldLabel: 'Product Price Basis', reason: priceBasisError, specLabel, specType });
    }
  }

  state.rows.forEach((row, rowIndex) => {
    const errors = isThreshold
      ? validateRowAmount(row as ThresholdFormulaRow, formulaType)
      : validateRangeRow(row as RangeFormulaRow, formulaType);
    const rowSuffix = isThreshold
      ? ` (${(row as ThresholdFormulaRow).adjustmentMode === AdjustmentMode.Discount ? 'Discount' : 'Premium'} side)`
      : ` (Range ${rowIndex + 1})`;

    if (!isThreshold && errors.upperBound !== undefined) {
      missingFields.push({ fieldLabel: `Bounds${rowSuffix}`, reason: errors.upperBound, specLabel, specType });
    }
    if (errors.amount) {
      missingFields.push({
        fieldLabel: `${amountConfig.label}${rowSuffix}`,
        reason: errors.amount,
        specLabel,
        specType,
      });
    }
    if (errors.perIncrement) {
      missingFields.push({ fieldLabel: `Per increment${rowSuffix}`, reason: errors.perIncrement, specLabel, specType });
    }
  });

  if (!isThreshold) {
    getRangeBoundaryIssues(state.rows as RangeFormulaRow[])
      .filter(issue => issue.type === 'overlap')
      .forEach(issue => {
        missingFields.push({
          fieldLabel: `Bounds (Range ${issue.rowIndex} / Range ${issue.rowIndex + 1})`,
          reason: issue.message,
          specLabel,
          specType,
        });
      });
  }

  return missingFields;
};

/**
 * Runs `getSpecValidationIssues` across every enabled spec, so "Test These Formulas" can be
 * blocked with a precise list of what's wrong instead of silently sending a zero-rate or
 * overlapping range to the API.
 */
export const validateEnabledSpecStates = (specStates: Record<SpecType, SpecState>): MissingFieldEntry[] => {
  const missingFields: MissingFieldEntry[] = [];

  SPEC_TYPES.forEach(spec => {
    const state = specStates[spec.key];
    if (!state.enabled) return;
    missingFields.push(...getSpecValidationIssues(spec.key, state));
  });

  return missingFields;
};
